import { FC, useState } from 'react';
import { Input } from '@ibsys/kit';

type Props = {
  label: string;
  /** Действующая граница — доля балла от максимума (0..1). */
  ratio: number;
  /** Новая граница долей балла, уже ограниченная диапазоном 0..1. */
  onChange: (ratio: number) => void;
};

/** Значащих цифр процента в поле: хватает любой введённой границы и отсекает шум двоичной дроби. */
const PERCENT_PRECISION = 12;

/**
 * Доля балла (0..1) → процент для показа в числовом поле — ровно той границей, что действует:
 * дробная граница показана дробной, а не округлённой до целого.
 */
const toPercent = (ratio: number): number => Number((ratio * 100).toPrecision(PERCENT_PRECISION));

/** Ограничивает процент диапазоном 0..100. */
const clampPercent = (pct: number): number => Math.min(100, Math.max(0, pct));

/**
 * Поле границы зоны качества: граница хранится долей, а показана и набирается процентом.
 *
 * Пока в поле набирают, в нём стоит набранный текст, а не граница, пересчитанная обратно из доли:
 * промежуточный ввод вроде «59.» иначе терял бы точку, и следующая цифра приклеивалась бы к целой
 * части («599»). Каждый разбираемый промежуточный ввод сразу становится границей; покинутое поле
 * показывает действующую границу.
 */
export const ZoneBoundaryInput: FC<Props> = ({ label, ratio, onChange }) => {
  const [draft, setDraft] = useState<string | null>(null);

  // Пустое поле границу не меняет — иначе `Number('')` сбросил бы её в 0.
  const handleChange = (raw: string) => {
    setDraft(raw);
    if (raw.trim() === '') return;
    const pct = Number(raw);
    if (!Number.isFinite(pct)) return;
    onChange(clampPercent(pct) / 100);
  };

  return (
    <Input
      type="number"
      size="sm"
      label={label}
      value={draft ?? toPercent(ratio)}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={() => setDraft(null)}
    />
  );
};
